// Free WhatsApp Click to Chat helpers (Owner direction, 2026-10-25) — wa.me deep links only;
// no WhatsApp Business API, no paid integration, no account required. Centralizes the
// international-number normalization and link-building previously duplicated across every
// sharing surface (POS, Sales History, Customer Profile, Wallet, Quotations).

// A customer's WhatsApp-capable number: the optional, separate `whatsapp_number` field when
// set, otherwise the regular saved `phone` — never assumes every phone number uses WhatsApp,
// but most do, so `phone` is a sensible default when no separate number was saved.
export const waNumberFor = (customer) => String(customer?.whatsapp_number || customer?.phone || "").trim();

// WhatsApp needs the full international number with no punctuation. Jamaica (and the rest of
// NANP) uses +1, so a bare 10-digit local number (876-555-2201) must become 18765552201 or
// WhatsApp can fail to resolve it / open the wrong chat. A number already typed with a country
// code (11+ digits, or starting with +) is passed through as typed.
export const waDigits = (raw) => {
  let digits = String(raw || "").replace(/\D/g, "");
  if (!digits) return null;
  if (digits.length === 10) digits = `1${digits}`;
  return digits.length >= 7 ? digits : null;
};

// Builds a wa.me Click to Chat link with a URL-encoded prefilled message. Returns null when
// there's no usable number — callers must never fall back to a free-text/arbitrary recipient.
export const waLink = (raw, message) => {
  const digits = waDigits(raw);
  if (!digits) return null;
  return `https://wa.me/${digits}?text=${encodeURIComponent(message || "")}`;
};

// Capitalizes a saved name for DISPLAY in a message only — never writes anything back to the
// customer record. Only straightens an ALL-CAPS or all-lowercase run (e.g. "JANE DOE" / "jane
// doe" → "Jane Doe"); a name that already has internal capitalization (e.g. "McDonald",
// "O'Brien", a company name like "NEEC") is left exactly as saved, since that's more likely
// intentional than a typo, and "safe" per Owner direction means never guessing wrong.
export const titleCaseForDisplay = (s) => String(s || "").trim().replace(/\S+/g, (w) => {
  if (/[a-z]/.test(w) && /[A-Z]/.test(w.slice(1))) return w;
  return w.slice(0, 1).toUpperCase() + w.slice(1).toLowerCase();
});

// One tasteful emoji per document heading — never more than one, per Owner direction.
const WA_HEADING_EMOJI = {
  receipt: "🧾", quote: "📝", payment: "💳", statement: "📑",
  deposit: "💰", wallet_statement: "📑", refund: "↩️", adjustment: "🔄",
};

// Professional WhatsApp Click to Chat message template (Owner direction, 2026-10-25) — every
// customer-facing message reads like real correspondence from an established business:
// *bold* is WhatsApp's own single-asterisk syntax (not markdown's **), the sending business's
// saved name opens (letterhead) and closes (signature) the message, a bold heading names the
// document, and a small, non-promotional "Powered by NEEC POS" line sits under the closing
// signature — never louder than the business's own identity, never a link or sales pitch.
// Every value passed in must already come from the authoritative document/ledger — this
// function only formats, it never computes or invents a figure, date or status.
export const waBusinessMessage = ({ business, kind, heading, meta = [], greeting, body = [], closing }) => {
  const biz = (business?.name || "").trim() || "Our Store";
  const emoji = WA_HEADING_EMOJI[kind] || "";
  return [
    `*${biz}*`,
    "",
    `*${heading.toUpperCase()}*${emoji ? ` ${emoji}` : ""}`,
    ...meta,
    "",
    `Hello${greeting ? ` ${titleCaseForDisplay(greeting)}` : ""},`,
    ...body,
    "",
    closing || "Thank you for your business.",
    "",
    biz,
    "_Powered by NEEC POS_",
  ].join("\n");
};
